import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Columns3,
  Square,
  Layers,
  Frame,
  Hammer,
  Library,
  Settings,
  ShieldCheck,
  ExternalLink,
  Wallet,
  Activity,
} from 'lucide-react';
import { Brand } from '@/components/Brand';
import { FootingIcon } from '@/components/icons/FootingIcon';
import { cn } from '@/lib/cn';
import { hasAnyPermission, useAuthStore, type AuthUser } from '@/stores/authStore';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  available: boolean;
  /** Permissions required to SHOW this item. Empty/undefined = always shown
   *  to any authenticated user. The user must have at least one. */
  requirePermissions?: string[];
}

const primaryNav: NavItem[] = [
  { to: '/app/dashboard', label: 'Inicio', icon: LayoutDashboard, available: true },
];

// Análisis estructural — calcula Mu/Vu/Pu desde cargas y los alimenta al
// módulo de Diseño correspondiente vía prefill. Apuntamos al hub para que
// el usuario vea todos los sub-módulos (vigas activo, columnas/losas/
// muros/zapatas como "soon").
const analysisNav: NavItem[] = [
  { to: '/app/analysis', label: 'Análisis estructural', icon: Activity, available: true, requirePermissions: ['modules.calc.read'] },
];

// Each module's sidebar entry now points to the *index* (saved designs
// list) instead of straight to the blank form. Users see their saved
// designs first and can create a new one from there.
const calcNav: NavItem[] = [
  { to: '/app/columns', label: 'Columnas', icon: Columns3, available: true, requirePermissions: ['modules.calc.read'] },
  { to: '/app/beams', label: 'Vigas', icon: Square, available: true, requirePermissions: ['modules.calc.read'] },
  { to: '/app/slabs', label: 'Losas', icon: Layers, available: true, requirePermissions: ['modules.calc.read'] },
  { to: '/app/walls', label: 'Muros', icon: Frame, available: true, requirePermissions: ['modules.calc.read'] },
  { to: '/app/footings', label: 'Zapatas', icon: FootingIcon, available: true, requirePermissions: ['modules.calc.read'] },
  { to: '/app/predim', label: 'Predimensionamiento', icon: Hammer, available: true, requirePermissions: ['modules.calc.read'] }, // index page = list of saved predim
];

const referenceNav: NavItem[] = [
  { to: '/app/normas', label: 'Normas y referencias', icon: Library, available: true, requirePermissions: ['normas.read'] },
];

/**
 * Items that require admin-level access. Each is shown only when the user
 * has at least one of the listed permissions. "Configuración" and "Facturación"
 * are for every authenticated user, so they don't restrict.
 */
const adminNav: NavItem[] = [
  {
    to: '/app/admin',
    label: 'Administración',
    icon: ShieldCheck,
    available: true,
    requirePermissions: ['users.read', 'roles.read', 'audit.read'],
  },
  { to: '/app/billing', label: 'Facturación', icon: Wallet, available: true },
  { to: '/app/settings', label: 'Configuración', icon: Settings, available: true },
];

/** Decide if a nav item should be visible for the current user. */
function filterByPerms(items: NavItem[], user: AuthUser | null): NavItem[] {
  return items.filter((it) => {
    if (!it.requirePermissions || it.requirePermissions.length === 0) return true;
    return hasAnyPermission(user, it.requirePermissions);
  });
}

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  // Resolve visible items per group based on the current user's permissions
  const visibleAnalysis = filterByPerms(analysisNav, user);
  const visibleCalc = filterByPerms(calcNav, user);
  const visibleReference = filterByPerms(referenceNav, user);
  const visibleAdmin = filterByPerms(adminNav, user);

  return (
    <aside
      className="flex w-60 shrink-0 flex-col border-r"
      style={{
        background: 'var(--surface-1)',
        borderColor: 'var(--border-subtle)',
      }}
    >
      <NavLink
        to="/app/dashboard"
        className="flex h-14 items-center border-b px-4 transition-colors hover:bg-[var(--surface-2)]"
        style={{ borderColor: 'var(--border-subtle)' }}
        aria-label="Ir al inicio"
      >
        {/* Horizontal lockup — shield + AlphaConcrete wordmark in a single
            PNG. The sidebar is 240px wide, so the asset has room to breathe. */}
        <Brand size="lg" />
      </NavLink>

      <nav className="flex-1 overflow-y-auto px-2 py-3 scrollbar-thin">
        <NavSection items={primaryNav} />
        {visibleAnalysis.length > 0 && <NavGroup label="Análisis" items={visibleAnalysis} />}
        {visibleCalc.length > 0 && <NavGroup label="Diseño estructural" items={visibleCalc} />}
        {visibleReference.length > 0 && <NavGroup label="Conocimiento" items={visibleReference} />}
        {visibleAdmin.length > 0 && <NavGroup label="Administración" items={visibleAdmin} />}
      </nav>

      <div className="border-t px-2 py-2" style={{ borderColor: 'var(--border-subtle)' }}>
        <NavLink
          to="/"
          end
          className="flex h-9 items-center gap-2.5 rounded-[var(--radius-sm)] px-3 text-sm transition-colors duration-200 hover:bg-[var(--surface-2)]"
          style={{ color: 'var(--text-secondary)' }}
        >
          <ExternalLink size={16} strokeWidth={1.6} aria-hidden />
          <span>Volver al sitio web</span>
        </NavLink>
        <div className="mt-2 px-3 text-[10.5px]" style={{ color: 'var(--text-muted)' }}>
          <div>v0.1.0 — dev</div>
          <div>E.060 + ACI 318</div>
        </div>
      </div>
    </aside>
  );
}

function NavGroup({ label, items }: { label: string; items: NavItem[] }) {
  return (
    <div className="mt-4">
      <div
        className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: 'var(--text-muted)' }}
      >
        {label}
      </div>
      <NavSection items={items} />
    </div>
  );
}

function NavSection({ items }: { items: NavItem[] }) {
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          {item.available ? (
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'group relative flex h-9 items-center gap-2.5 rounded-[var(--radius-sm)] px-3 text-sm transition-colors duration-200',
                  isActive
                    ? 'bg-[var(--surface-2)] text-[var(--text-primary)]'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--surface-2)] hover:text-[var(--text-primary)]',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r"
                      style={{ background: 'var(--accent)' }}
                    />
                  )}
                  <item.icon size={16} strokeWidth={1.6} aria-hidden />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          ) : (
            <div
              className="flex h-9 cursor-not-allowed items-center gap-2.5 rounded-[var(--radius-sm)] px-3 text-sm opacity-50"
              style={{ color: 'var(--text-muted)' }}
              title="Proximamente"
            >
              <item.icon size={16} strokeWidth={1.6} aria-hidden />
              <span>{item.label}</span>
              <span className="ml-auto text-[9px] uppercase tracking-wider" style={{ color: 'var(--text-disabled)' }}>
                soon
              </span>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
